import { useState } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion } from 'framer-motion';
import { Sun, Moon, Loader2, Coffee, Play } from 'lucide-react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const AVATAR_COLORS = [
  { bg: 'bg-blue-100', text: 'text-blue-700' },
  { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  { bg: 'bg-amber-100', text: 'text-amber-700' },
  { bg: 'bg-rose-100', text: 'text-rose-700' },
  { bg: 'bg-violet-100', text: 'text-violet-700' },
  { bg: 'bg-cyan-100', text: 'text-cyan-700' },
  { bg: 'bg-orange-100', text: 'text-orange-700' },
  { bg: 'bg-pink-100', text: 'text-pink-700' },
  { bg: 'bg-teal-100', text: 'text-teal-700' },
  { bg: 'bg-indigo-100', text: 'text-indigo-700' },
];

function getAvatarColor(name = '') {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(firstName, lastName) {
  const first = (firstName || '')[0] || '';
  const last = (lastName || '')[0] || '';
  return (first + last).toUpperCase() || '?';
}

/**
 * Format a duration in milliseconds as "Xh Ym".
 */
function formatDuration(ms) {
  if (!ms || ms <= 0) return '0m';
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) return `${minutes}m`;
  return `${hours}h ${minutes}m`;
}

/**
 * Format a timestamp for display (e.g. "06:19 AM").
 */
function formatClockInTime(isoOrDate) {
  if (!isoOrDate) return '';
  const d = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate;
  return d.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// KioskClockInOut
// ---------------------------------------------------------------------------
// The action screen that shows either a "Clock In" or "Clock Out" button
// depending on the user's current clock-in status.
//
// Props:
//   user       (object)   - selected user
//   clockedIn  (boolean)  - whether user is currently clocked in
//   onClockIn  (function) - async function to clock in
//   onClockOut (function) - async function to clock out
//   onSuccess  (function) - called with { type, userName, time, duration }
//   onError    (function) - called with error message
//   onBack     (function) - go back
// ---------------------------------------------------------------------------

export default function KioskClockInOut({
  selectedUser,
  clockedIn = false,
  onBreak = false,
  activeBreak = null,
  onClockIn,
  onClockOut,
  onStartBreak,
  onEndBreak,
  identifyMethod,
}) {
  const [processingAction, setProcessingAction] = useState(null); // null | 'clock' | 'break'

  if (!selectedUser) return null;

  const user = selectedUser;
  const displayName =
    user.display_name ||
    `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
    'Unknown';

  const color = getAvatarColor(
    `${user.first_name || ''}${user.last_name || ''}`,
  );
  const initials = getInitials(user.first_name, user.last_name);

  // -------------------------------------------------------------------------
  // Compute duration and clock-in time when clocked in
  // -------------------------------------------------------------------------
  const clockInTime = user.clocked_in_at || user.clock_in_time || null;
  const durationMs = clockInTime
    ? Date.now() - new Date(clockInTime).getTime()
    : 0;

  // Break duration calculation
  const breakStartTime = activeBreak?.start_time || null;
  const breakDurationMs = breakStartTime
    ? Date.now() - new Date(breakStartTime).getTime()
    : 0;

  // -------------------------------------------------------------------------
  // Handle the clock action
  // -------------------------------------------------------------------------
  const handleAction = async () => {
    if (processingAction) return;
    setProcessingAction('clock');

    try {
      if (clockedIn) {
        await onClockOut?.(user.id);
      } else {
        await onClockIn?.(user.id, identifyMethod);
      }
    } catch {
      // Parent handles error display
    } finally {
      setProcessingAction(null);
    }
  };

  const handleBreakAction = async () => {
    if (processingAction) return;
    setProcessingAction('break');

    try {
      if (onBreak) {
        await onEndBreak?.(user.id);
      } else {
        await onStartBreak?.(user.id);
      }
    } catch {
      // Parent handles error display
    } finally {
      setProcessingAction(null);
    }
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-6 px-6 py-6">
        {/* Avatar + name */}
        <motion.div
          className="flex items-center gap-4"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {user.avatar_url ? (
            <img
              src={user.avatar_url}
              alt=""
              className="w-14 h-14 rounded-full object-cover shadow ring-2 ring-white"
            />
          ) : (
            <div
              className={`w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold shadow ring-2 ring-white ${color.bg} ${color.text}`}
            >
              {initials}
            </div>
          )}
          <span className="text-2xl font-semibold text-slate-800">
            {displayName}
          </span>
        </motion.div>

        {/* Status text */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.3 }}
        >
          {clockedIn ? (
            <>
              {onBreak ? (
                <p className="text-xl text-amber-600 font-medium">
                  On break for{' '}
                  <span className="font-bold">
                    {formatDuration(breakDurationMs)}
                  </span>
                </p>
              ) : (
                <p className="text-xl text-slate-600">
                  You have been on site for{' '}
                  <span className="font-bold text-slate-800">
                    {formatDuration(durationMs)}
                  </span>
                </p>
              )}
              {clockInTime && (
                <p className="mt-1 text-lg text-slate-500">
                  Clocked in at {formatClockInTime(clockInTime)}
                </p>
              )}
            </>
          ) : (
            <p className="text-xl text-slate-600">
              You are not currently clocked in
            </p>
          )}
        </motion.div>

        {/* Action buttons */}
        <div className="mt-4 w-full max-w-md flex flex-col gap-3">
          {/* Break button (only when clocked in) */}
          {clockedIn && (
            <motion.button
              type="button"
              className={
                'w-full h-16 rounded-2xl text-xl font-bold shadow-md flex items-center justify-center gap-3 transition-colors disabled:opacity-60 ' +
                (onBreak
                  ? 'bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-700'
                  : 'bg-amber-500 text-white hover:bg-amber-600 active:bg-amber-700')
              }
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.35 }}
              whileTap={processingAction ? {} : { scale: 0.95 }}
              onClick={handleBreakAction}
              disabled={!!processingAction}
              aria-label={onBreak ? 'End break' : 'Start break'}
            >
              {processingAction === 'break' ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : onBreak ? (
                <>
                  <Play className="w-6 h-6" />
                  End Break
                </>
              ) : (
                <>
                  <Coffee className="w-6 h-6" />
                  Start Break
                </>
              )}
            </motion.button>
          )}

          {/* Clock in/out button (hidden when on break -- must end break first) */}
          {!onBreak && (
            <motion.button
              type="button"
              className={
                'w-full h-20 rounded-2xl text-2xl font-bold shadow-lg flex items-center justify-center gap-3 transition-colors disabled:opacity-60 ' +
                (clockedIn
                  ? 'bg-rose-500 text-white hover:bg-rose-600 active:bg-rose-700'
                  : 'bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800')
              }
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.35 }}
              whileTap={processingAction ? {} : { scale: 0.95 }}
              onClick={handleAction}
              disabled={!!processingAction}
              aria-label={clockedIn ? 'Clock out' : 'Clock in'}
            >
              {processingAction === 'clock' ? (
                <Loader2 className="w-7 h-7 animate-spin" />
              ) : clockedIn ? (
                <>
                  <Moon className="w-7 h-7" />
                  Clock Out
                </>
              ) : (
                <>
                  <Sun className="w-7 h-7" />
                  Clock In
                </>
              )}
            </motion.button>
          )}
        </div>
    </div>
  );
}
